import type { Dashboard, ResolvedReason, Severity } from '@ametica/shared';

/**
 * Závažnosť je stavová paleta a nasadzuje sa VŽDY s popiskom — farba samotná
 * nestačí (spec, Frontend). Tokeny sú v app.css (`sev-*`), tu je len mapovanie.
 */
export const SEVERITY: Record<Severity, { label: string; bg: string; text: string; border: string }> = {
  critical: { label: 'Kritická', bg: 'bg-sev-critical', text: 'text-sev-critical', border: 'border-sev-critical' },
  high: { label: 'Vysoká', bg: 'bg-sev-high', text: 'text-sev-high', border: 'border-sev-high' },
  medium: { label: 'Stredná', bg: 'bg-sev-medium', text: 'text-sev-medium', border: 'border-sev-medium' },
  low: { label: 'Nízka', bg: 'bg-sev-low', text: 'text-sev-low', border: 'border-sev-low' },
  unconfirmed: { label: 'Nepotvrdená', bg: 'bg-sev-unconfirmed', text: 'text-sev-unconfirmed', border: 'border-sev-unconfirmed' },
};

export const RESOLVED_REASON: Record<ResolvedReason, string> = {
  PATCHED: 'opravené',
  REMOVED: 'komponent odstránený',
  WITHDRAWN: 'stiahnuté z katalógu',
};

export function pluralFindings(n: number): string {
  return n === 1 ? 'zraniteľnosť' : n >= 2 && n <= 4 ? 'zraniteľnosti' : 'zraniteľností';
}

export function pluralSites(n: number): string {
  return n === 1 ? 'stránke' : 'stránkach';
}

export type FreshnessTone = 'ok' | 'stale' | 'bad' | 'none';
export interface Freshness {
  tone: FreshnessTone;
  label: string;
}

const HOUR = 3600_000;
/** production feed ide 1×/deň — po 26 h bez úspechu jeden beh chýba (rovnaké prahy ako v Nastaveniach) */
export const CATALOG_STALE_MS = 26 * HOUR;
export const CATALOG_OLD_MS = 72 * HOUR;
/** stránky sa skenujú každých 6 h — po 7 h chýba beh; po 24 h je report zastaraný (ako `connection: stale`) */
export const SCAN_STALE_MS = 7 * HOUR;
export const SCAN_OLD_MS = 24 * HOUR;

function freshness(iso: string | null, staleMs: number, oldMs: number, what: string, verb: string, now: number): Freshness {
  if (!iso) return { tone: 'none', label: `${what} ešte ${verb === 'synchronizovaný' ? 'nebol' : 'neboli'} ${verb}` };
  const age = now - new Date(iso).getTime();
  if (age > oldMs) return { tone: 'bad', label: `${what} ${verb} ${ago(age)} — starý stav` };
  if (age > staleMs) return { tone: 'stale', label: `${what} ${verb} ${ago(age)} — mešká` };
  return { tone: 'ok', label: `${what} ${verb} ${ago(age)}` };
}

/**
 * Dve rôzne aktuálnosti, zámerne oddelené: katalóg (FeedSyncRun) a stránky (ScanRun).
 * „Katalóg starý 4 h" a „stránky skenované pred 10 min" nie je to isté.
 */
export function catalogFreshness(d: Pick<Dashboard, 'catalog'>, now = Date.now()): Freshness {
  if (d.catalog.total === 0) return { tone: 'bad', label: 'Katalóg je prázdny — nálezy nemôžu vzniknúť' };
  return freshness(d.catalog.lastSyncAt, CATALOG_STALE_MS, CATALOG_OLD_MS, 'Katalóg', 'synchronizovaný', now);
}

export function scansFreshness(d: Pick<Dashboard, 'scans' | 'sites'>, now = Date.now()): Freshness {
  if (d.sites.total === 0) return { tone: 'none', label: 'Zatiaľ žiadna stránka' };
  return freshness(d.scans.lastReportAt, SCAN_STALE_MS, SCAN_OLD_MS, 'Stránky', 'skenované', now);
}

function ago(ms: number): string {
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'práve teraz';
  if (min < 60) return `pred ${min} min`;
  const h = Math.round(ms / HOUR);
  if (h < 48) return `pred ${h} h`;
  return `pred ${Math.round(ms / (24 * HOUR))} dňami`;
}
